import React, {useState, useEffect, useRef, lazy, Suspense} from 'react';
import {Link} from "react-router";
import {useDispatch, useSelector} from 'react-redux';
import { useSearchParams } from 'react-router';
import {useForm} from 'react-hook-form'
import * as yup from 'yup'
import {yupResolver} from "@hookform/resolvers/yup";
import store from "../../reduxStore/store";
import { setTab, setLeadForm, setAppointmentForm, setAppointmentInfo, setOutsideParamFlag } from "../../reduxStore/appointmentScheduleSlice";
import phoneValidationClient from '../../utility/apiClients/user/phoneValidationClient';
import getAppointmentsClient, { getShowScheduleClient } from '../../utility/apiClients/appointment-schedule/getAppointmentsClient';
import saveUserInformationClient from '../../utility/apiClients/appointment-schedule/saveUserInformationClient';
import saveAppointmentsClient from '../../utility/apiClients/appointment-schedule/saveAppointmentsClient';
import serviceEmailClient from '../../utility/apiClients/appointment-schedule/serviceEmailClient';
import {LocalBranchInfoClient} from '../../utility/apiClients/appointment-schedule/localBranch';
import validatePostalCodeClient from '../../utility/apiClients/appointment-schedule/validatePostalCodeClient';

import Globals from '../../utility/constants/appConstants';
import OverlayLoader from '../loader/overlayLoader';
import { encryptData } from '../../utility/utils/hashing';
import { getVisitId, startVisitIdSync } from '../../utility/features/helpers/visitIdHelper';

import AddressSuggestion from "./addressSuggestion";
import "../../styles/appointmentSchedule/appointmentSchedule.css"

import { getCookie, checkCookie } from '../../utility/utils/cookie';

const ReCAPTCHA = lazy(() => import('react-google-recaptcha'));

/* only time blocks with open seats and isAvailable=true can be booked */
const getBookableBlocks = (dateInfo) => (dateInfo?.availableTimeBlocks || [])
  .filter((t) => parseInt(t.availableAppointments) > 0 && String(t.isAvailable).toLowerCase() === "true");

/* keep only shows/dates that still have bookable time blocks */
const getShowsWithSlots = (shows) => shows
  .map((s) => ({ ...s, availableDates: (s.availableDates || []).filter((d) => getBookableBlocks(d).length > 0) }))
  .filter((s) => s.availableDates.length > 0);

/* appointmentDate is an ISO string with offset, e.g. "2026-11-02T00:00:00-05:00"; read the calendar date from its first 10 chars */
const weekDayOf = (iso) => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short' });
};

const AppointmentSchedule = () => {
  const dispatch = useDispatch();
  const appointmentScheduleJSON = Globals.appointmentSchedule;
  const selAdd = store.getState().accountSummary.selectedServiceAddress;
  const {tab, leadForm, appointmentForm, appointmentInfo, outsideParamFlag} = useSelector((state) => state.appointmentSchedule);
  const leadFormRef = useRef(null);
  const appointmentFormRef = useRef(null);
  const leadFormBtnRef = useRef(null);
  const appointmentFormBtnRef = useRef(null);
  const recaptchaRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const showLoader = () => setIsLoading(true);
  const hideLoader = () => setIsLoading(false);
  const [queryParams] = useSearchParams();
  const customerNumber = selAdd ? selAdd.split(" | ")?.[1] : localStorage.getItem("customerNumbers") ? JSON.parse(localStorage.getItem("customerNumbers"))[0] : '';

  const googleCidVal = () => {
    let cookieGa = checkCookie("_ga")?getCookie("_ga"):'5555555555.5555555555';
    return cookieGa;
  };

  /* Read params from POST (stored in localStorage by /api/appointment-post) — consume once then remove */
  const [postParams] = useState(() => {
    if (typeof window !== 'undefined' && localStorage.getItem('appt_post_params')) {
      try {
        const data = JSON.parse(atob(localStorage.getItem('appt_post_params')));
        return data;
      } catch (e) {
        console.error("Error parsing appointment POST params from localStorage", e);
      }
    }
    return null;
  });

  const outsideParams = {
    address: queryParams.get('address') ? decodeURI(queryParams.get('address')) : "",
    fname: queryParams.get('fname') ? queryParams.get('fname') : "",
    lname: queryParams.get('lname') ? queryParams.get('lname') : "",
    email: queryParams.get('email') ? queryParams.get('email') : "",
    phone: queryParams.get('phone') ? queryParams.get('phone') : "",
    repId: queryParams.get('repId') ? queryParams.get('repId') : "",
    scheduler_source: queryParams.get('scheduler_source') ? queryParams.get('scheduler_source') : "",
  };

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    address: "",
    street: "",
    city: "",
    state: "",
    zipcode: "",
    branch: "",
    phone: "",
    email: "",
    connectBy: "",
    appointmentDate : "",
    appointmentTime : {},
    showID: "",
    stepComplete: 0,
    scheduler_source: ""
  });
  const [userRecordID, setUserRecordID] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [isAddressSelected, setIsAddressSelected] = useState(false);
  const [invalidAddressError, setInvalidAddressError] = useState(false);
  const [existingAppointmentNotice, setExistingAppointmentNotice] = useState('');
  const captchaSiteKey = ``;
  
  const formSchema = yup.object().shape({
    first_name: yup.string().required("First name is required").label("First Name"),
    last_name: yup.string().required("Last name is required").label("Last Name"),
    address: yup.string().required("Address is required").label("Address"),
    email: yup.string().email("Invalid Email").required("Email Address is required").matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Please enter a valid email address e.g. john.doe@gmail.com").label("Email"),
    phone: yup.string().required("Phone is required").matches(/(\+1)?\(?\d{3}\)?\-?\d{3}\-?\d{4}/, "Invalid phone.").label("Phone"),
    company_name: yup.string().required("Company name is required").label("Company Name")
  });

  

  useEffect(() => {
    let addressInput = null;
    let addressInputOnKeyDownCallBack = null;

    localStorage.setItem('EDQValueSelected', 'No');
    localStorage.setItem('EDQValueModified', 'No');
    /* phone input */
    const phoneInput = leadFormRef.current.querySelector("[name=phone]");

    (async () => {
      const inputmaskModule = await import('inputmask');
      const Inputmask = inputmaskModule.default || inputmaskModule;
      Inputmask({ "mask": "(999)-999-9999" }, { showMaskOnFocus: true }).mask(phoneInput);
    })();

    // Keep visitID synchronized from Infinity hidden input and storage.
    startVisitIdSync();

    /* enter key selection */
    const elemOnSite = document.getElementsByClassName('on-site').length > 0?document.getElementsByClassName('on-site')[0]:""; 
    const elemPhoneCall = document.getElementsByClassName('phone-call').length > 0?document.getElementsByClassName('phone-call')[0]:""; 
    document.addEventListener('keypress', event => {
      if (event.keyCode == 13) {
        let curEle = "";
        if (elemOnSite === document.activeElement || elemPhoneCall === document.activeElement) { 
          curEle = elemOnSite === document.activeElement?elemOnSite:elemPhoneCall;
          /* trigger click to select */
          curEle.querySelector("input[type=radio]").click();
        } else if(document.activeElement.classList.contains('date') && document.getElementsByClassName('date').length > 0){
          Object.values(document.getElementsByClassName('date')).forEach(element => {
            if(element === document.activeElement){
              curEle = element;
              /* trigger click to select */
              curEle.querySelector("input[type=radio]").click();
            }
          });
        } else if(document.activeElement.classList.contains('time') && document.getElementsByClassName('time').length > 0){
          Object.values(document.getElementsByClassName('time')).forEach(element => {
            if(element === document.activeElement){
              curEle = element;
              /* trigger click to select */
              curEle.querySelector("input[type=checkbox]").click();
            }
          });
        }
      }
    });
    /* address input */
    addressInput = leadFormRef.current.querySelector("#addressSearch");
    addressInputOnKeyDownCallBack = (e) => {
      localStorage.setItem('EDQValueSelected', 'No');
      localStorage.setItem('EDQValueModified', 'Yes');
    };
    addressInput?.addEventListener('keydown', addressInputOnKeyDownCallBack);

    return () => {
      if (addressInput && addressInputOnKeyDownCallBack) {
        addressInput.removeEventListener('keydown', addressInputOnKeyDownCallBack);
      }
    };
  }, []);

  useEffect(() => {
    if(tab == 'appointment' && form.appointmentDate){
      /* on each date selection by default all time will be unselected */
      Object.values(document.querySelectorAll(`#${appointmentFormRef.current.id} input[type=checkbox]`)).map((itm) => itm.checked = false);
    }
  }, [form.appointmentDate]);

  /* page should always scroll to top after each step */
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    /* Thank You */
    if(form.stepComplete == 2 && (form.connectBy == "onsite" || form.connectBy == "phonecall")){
      const formUserSelection = form.connectBy == "onsite"?"Schedule In-Person Appt":"Schedule Call";
      //GA Implementation
      if(global.window){
        const currentURL = window.location.href;
        //const current_title = document.title;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'event': 'form_submission',
          'form_title': outsideParams.address ? "TGResInternalApptScheduler" : "TGResiAppointmentScheduler",    
          'form_user_selection': formUserSelection,
          'page_title': "Appointment Scheduler | TruGreen",
          'page_location': currentURL,
        });
      }
    }

  }, [form.stepComplete]);

  useEffect(() => {
   
    /* verify form data with field input value */
    const varifiedFormData = verifyFormDataWithFieldValue();
    
    /* save data */
    dispatch(setLeadForm({
      first_name: varifiedFormData.first_name,
      last_name: varifiedFormData.last_name,
      street: varifiedFormData.street,
      city: varifiedFormData.city,
      state: varifiedFormData.state,
      zipcode: varifiedFormData.zipcode,
      phone: varifiedFormData.phone,
      email: varifiedFormData.email,
      connectBy: form.connectBy 
    }));
  }, [form]);

  const {register, handleSubmit, setError, getValues, clearErrors, formState: {errors}, setValue} = useForm({
      mode: 'all',
      resolver: yupResolver(formSchema)
  });

  /* Pre-fill form from POST params (cookie-based) */
  useEffect(() => {
    if (!postParams) return;
    setForm((prev) => ({
      ...prev,
      "address": postParams.address,
      "first_name": postParams.fname,
      "last_name": postParams.lname,
      "phone": postParams.phone.slice(0, 10),
      "email": postParams.email,
      "scheduler_source": postParams.scheduler_source
    }));
    setValue('first_name', postParams.fname);
    setValue('last_name', postParams.lname);
    setValue('phone', postParams.phone);
    setValue('email', postParams.email);
    setValue('address', postParams.address);
    setValue('connectBy', 'onsite');
    setIsAddressSelected(true);
    dispatch(setOutsideParamFlag(true));
  }, [postParams]);

  useEffect(() => {
    if(queryParams.toString() !== '') {
    setForm({
      ...form,
      "address": outsideParams.address,
      "first_name": outsideParams.fname,
      "last_name": outsideParams.lname,
      "phone": outsideParams.phone.slice(0,10),
      "email": outsideParams.email,
      "scheduler_source": outsideParams.scheduler_source
    })

    setValue('first_name', outsideParams.fname)
    setValue('last_name', outsideParams.lname)
    setValue('phone', outsideParams.phone)
    setValue('email', outsideParams.email)
    setValue('address', outsideParams.address)
    setValue('connectBy', 'onsite')
    dispatch(setOutsideParamFlag(true));
  }
  }, [queryParams])

  const formSchemaAppointment = yup.object().shape({
    appointmentDate: yup.string().required("Required").label("Appointment Date"),
    appointmentTime: yup.string().required("Required").label("Appointment Time")
  });

  const {register: registerAppointment, handleSubmit: handleSubmitAppointment, setError: setErrorAppointment, getValues:getValuesAppointment, clearErrors:clearErrorsAppointment, formState: {errors: errorsAppointment}, setValue: setValueAppointment} = useForm({
      mode: "onChange",
      defaultValues: {
        appointmentDate: "",
        appointmentTime: ""
      },
      resolver: yupResolver(formSchemaAppointment)
  });
  
    const scheduleDateFormat = (dateStr = "") => {
    if (!dateStr) return "";
    let dateObj;
    // If format is YYYY-MM-DD, parse as local date
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      dateObj = new Date(year, month - 1, day);
    } else {
      dateObj = new Date(dateStr);
    }
    if (isNaN(dateObj.getTime())) return "";

    const monthName = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthName[dateObj.getMonth()];
    const day = dateObj.getDate();

    return `${month} ${day}`;
  }

  const scheduleTime = (time = "") => {
    const timeString = time;

    // Create a Date object. We can use an arbitrary date, as we only care about the time.
    // It's important to include a date to create a valid Date object.
    const dummyDate = new Date(`2000-01-01T${timeString}`);

    // Use toLocaleTimeString to format the time into a locale-sensitive string with AM/PM.
    // 'en-US' is used for US English formatting, which includes AM/PM.
    // The options object specifies to display only the hour and minute in 12-hour format with AM/PM.
    const formattedTime = dummyDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true // Ensures 12-hour format with AM/PM
    });
    return formattedTime;
  }

  const verifyFormDataWithFieldValue = () => {
    const selectedAddress = document.querySelector("[name=address]") != null?document.querySelector("[name=address]").value:"";

    let address_area = [], street_addr = "", city_state_zip_arr = [], zipcode = "", statecode = "", cityname = "";
    if(selectedAddress != ""){
      address_area = selectedAddress.split(',');
      street_addr = address_area.shift();
      city_state_zip_arr = address_area.join().replace(/\,/g,"").split(" ");
      zipcode = (city_state_zip_arr.pop() ?? "").trim();
      statecode = (city_state_zip_arr.pop() ?? "").trim();
      cityname = city_state_zip_arr.join(" ").trim();
    }

    return {
      "first_name": form.first_name != ""?form.first_name:(document.querySelector("[name=first_name]") != null?document.querySelector("[name=first_name]").value:""),
      "last_name": form.last_name != ""?form.last_name:(document.querySelector("[name=last_name]") != null?document.querySelector("[name=last_name]").value:""),
      "street": form.street != ""?form.street:street_addr,
      "city": form.city != ""?form.city:cityname,
      "state": form.state != ""?form.state:statecode,
      "zipcode": form.zipcode != ""?form.zipcode:zipcode,
      "email": form.email != ""?form.email:(document.querySelector("[name=email]") != null?document.querySelector("[name=email]").value:""),
      "phone": form.phone != ""?form.phone:(document.querySelector("[name=phone]") != null?document.querySelector("[name=phone]").value:""),
      "company_name": form.company_name != ""?form.company_name:(document.querySelector("[name=company_name]") != null?document.querySelector("[name=company_name]").value:"")
    };
  }

  const preparePostData = (leadType) => {
    /* verify form data with field input value */
    const varifiedFormData = verifyFormDataWithFieldValue();
    const repId = outsideParams.repId || postParams?.repId || postParams?.RepID || "";

    return {
        "referEmpNumber": "",
        "firstName": varifiedFormData.first_name,
        "lastName": varifiedFormData.last_name,
        "streetAddress": encryptData(varifiedFormData.street + `__apptvalue${Date.now()}`, "appointmentScheduler"),
        "city": varifiedFormData.city,
        "state": varifiedFormData.state,
        "postalCode": varifiedFormData.zipcode,
        "country": "US",
        "emailAddress": varifiedFormData.email,
        "phone1": varifiedFormData.phone,
        "googleCid": googleCidVal(),
        "Comments": "",
        "ReferringURL": window.location?window.location.origin+window.location.pathname:"",
        "LandingID": window.location?window.location.origin+window.location.pathname:"",
        "UserAgentData": window.navigator?window.navigator?.userAgent:"",
        "CustomerNumber": customerNumber,
        "BrandNonBrand": "",
        "EDQModified": localStorage.getItem('EDQValueModified'),
        "EDQSelected": localStorage.getItem('EDQValueSelected'),
        "VisitID": getVisitId(),
        "VendorLeadID": "",
        "RepID": repId,
        "scheduler_source": form.scheduler_source ? form.scheduler_source : "",
        "leadType": leadType
    };
  }

  const onSubmit = async (data) => {
    /* if address not selected from the suggestion list then display error */
    if(invalidAddressError) {
      setError("address1", { type: "manual", message: `Invalid address. Please enter a valid address.` });
      return false;
    } else if(!isAddressSelected){
      setError("address1", { type: "manual", message: `Street Address must be selected from the drop down.` });
      return false;
    } 
    

    /* check if phone number is invalid then display error */
    try {
      await phoneValidationClient(form.phone.replace(/[^\d]+/g, '').trim());
    } catch (error) {
      setError("phone", { type: "manual", message: `Invalid phone` });
      return false;
    }

    const onSubmitStep1 = async () => { 
      /* save lead information */
      try {
        showLoader();
        /* call step 2 appointment availability */
        const showRes = await getShowScheduleClient();
        const shows = getShowsWithSlots(Array.isArray(showRes.data) ? showRes.data : []);
        dispatch(setAppointmentInfo(shows));

        hideLoader();

        if (shows.length > 0) {
          /* will show appointment form */
          dispatch(setTab('appointment'));

          /* set form state as submitted */
          setForm({
            ...form,
            showID: shows[0].showID,
            stepComplete: 1
          });
        } else {
          /* set form state as submitted and display thankyou */
          setForm({
            ...form,
            stepComplete: 2,
            connectBy: "no_schedule"
          });
        }
      } catch(err){
        hideLoader();
        /* if zipcode is not service able */
        setError("connectBy", { type: "manual", message: `Unfortunately we currently do not service that zipcode` });
        kickOutCall("ERROR : Some error occured.", JSON.stringify({
          "form" : form
        }), JSON.stringify(err));
        return false;
      }
    }
    onSubmitStep1();
  }

  const onSubmitAppointment = async (data) => {
    clearErrorsAppointment('appointmentDate'); 
    clearErrorsAppointment('appointmentTime'); 
    
    /* save data */
    dispatch(setAppointmentForm({
        ...data
    }));

    /* call step 2 user info */
    try {
      showLoader();
      const resUserInfo = await saveAppointmentsClient({
          "showID": form.showID,
          "apptDate": form.appointmentDate.date.slice(0, 10),
          "startTime": form.appointmentTime.startTime.toLowerCase(),
          "endTime": form.appointmentTime.endTime.toLowerCase()
      });
      
      /* if slot successfully booked */
      if(resUserInfo.data && resUserInfo.data.success == true){
        /* set form state as submitted and display thankyou */
        // the internal service email api call should be done here
        try {
          await serviceEmailClient({
            "ToAddress": appointmentScheduleJSON.EmailToAddress,
            "fromAddress": appointmentScheduleJSON.EmailFromAddress,
            "fromName": appointmentScheduleJSON.EmailFromName,
            "subject": `NEW Commercial Lead(${form.first_name}, ${form.last_name}) Submitted`,
            "body": `First Name: ${form.first_name}<br/>Last Name: ${form.last_name}<br/>Company Name: ${form.company_name || ""}<br/>Business address: ${form.address}<br/>Email: ${form.email}<br/>Phone number: ${form.phone}<br/>Appointment Venue: ${selectedShow?.name}<br/>Date & Time: ${form.appointmentDate.weekDay}, ${scheduleDateFormat(form.appointmentDate.date.slice(0, 10))} ${scheduleTime(form.appointmentTime.startTime).toLowerCase()}-${scheduleTime(form.appointmentTime.endTime).toLowerCase()}`
          });
          hideLoader();
        } catch(emailErr){
          console.log("ERROR : serviceEmailClient", emailErr);
          hideLoader();
        }
        setForm({
            ...form,
            stepComplete: 2,
            connectBy: "success"
          });
      } else {
        hideLoader();
        setErrorAppointment("appointmentTime", { type: "manual", message: resUserInfo.data.message });
        return false;
      }
    } catch(err){ 
      let bookingError = err?.response?.data;
      if (typeof bookingError === "string") {
        try { bookingError = JSON.parse(bookingError); } catch { bookingError = null; }
      }
      if (bookingError?.success === false) {
        hideLoader();
        setErrorAppointment("appointmentTime", { type: "manual", message: bookingError.message });
        return false;
      }
      hideLoader();
    }
  }

  const displayStep = (step) => {
    
    if(form.stepComplete > 0) return false;
    dispatch(setTab(step));
  }


  const handleForm = (e = null, type, value = null) => {
    /* trim value */
    let val = value === null ? e.target.value : value;

    /* created new form object with manipulated data */
    let formObj = {
      ...form,
      [type]: val
    };
    
    /* save */
    setForm(formObj);
  }

  const onAddressSuggestionSelected = (suggestion, addressSelected = false) => {
    
    const selectedAddress = suggestion.address;

    let address_area = selectedAddress.split(',');
    let street_addr = address_area.shift();
    let city_state_zip_arr = address_area.join().replace(/\,/g,"").split(" ");
    let zipcode = city_state_zip_arr && city_state_zip_arr.pop().trim();
    let statecode = city_state_zip_arr && city_state_zip_arr.pop().trim();
    let cityname = city_state_zip_arr && city_state_zip_arr.join(" ").trim();

    let formObj = {
      ...form,
      "address": selectedAddress,
      "street": street_addr,
      "city": cityname,
      "state": statecode,
      "zipcode": zipcode
    };
    
    clearErrors('address');
    clearErrors('address1');
    /* save */
    setValue("address", selectedAddress);
    setForm(formObj);
    setIsAddressSelected(true);
  }

  const handleDateSelection = (dateInfo) => {
    let formObj = {
      ...form,
      "appointmentDate": {"date" : dateInfo.appointmentDate, "weekDay": weekDayOf(dateInfo.appointmentDate)},
      "appointmentTime": {}
    };
    setForm(formObj);
    setValueAppointment('appointmentDate', JSON.stringify({"date" : dateInfo.appointmentDate, "weekDay": weekDayOf(dateInfo.appointmentDate)})); 
    clearErrorsAppointment('appointmentDate'); 
    setValueAppointment('appointmentTime', JSON.stringify({}));
    clearErrorsAppointment('appointmentTime'); 
  }

  const handleShowSelection = (show) => {
    setForm({ ...form, showID: show.showID, appointmentDate: "", appointmentTime: {} });
    setValueAppointment('appointmentDate', "");
    setValueAppointment('appointmentTime', "");
    clearErrorsAppointment();
  }

  const handleTimeSelection = (timeInfo) => {
    handleForm(null, 'appointmentTime', timeInfo); 
    setValueAppointment('appointmentTime', JSON.stringify(timeInfo)); 
    clearErrorsAppointment('appointmentTime');
  }

  const kickOutCall = async (error = '', apiRequest = null, apiResponse = null) => {
    if(error === "" || error === null) return false;

    try {
      const kickOutApiEndpoint = `${process.env.REACT_APP_ECOMM_URL}/kickout/appendlog`;
      let payload = {
        logMsg: error,
        logData: {
          apiRequest: apiRequest,
          apiResponse: apiResponse
        },
        browser: navigator.userAgent,
        os: navigator.platform,
        screen: `${window.screen.width}x${window.screen.height}`,
        network: navigator.onLine ? 'online' : 'offline',
        url: window.location.href,
        timestamp: new Date().toISOString()
      }

      /* get _ga id */
      let cookieGa = checkCookie("_ga")?getCookie("_ga"):'';
      /* get session id */
      if(localStorage.getItem('analyticsSessionId') === null || localStorage.getItem('analyticsSessionId') === ""){
        const newTimeStampValue = new Date().getTime().toString() +""+ Math.floor(Math.random()*1000000).toString();
        payload["SessionID"] = newTimeStampValue+"-"+cookieGa;
        localStorage.setItem("analyticsSessionId", newTimeStampValue);
      } else {
        payload["SessionID"] = localStorage.getItem('analyticsSessionId')+"-"+cookieGa;
      }
      
      await fetch(kickOutApiEndpoint, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    } catch (err) {
      console.log(`Error in kickout log api call: ${err}`);
    }
  }

  /* parse Store Data */
  const storeAppointmentDate = appointmentForm.appointmentDate != ""?JSON.parse(appointmentForm.appointmentDate):"";
  const storeAppointmentTime = appointmentForm.appointmentTime != ""?JSON.parse(appointmentForm.appointmentTime):"";

  /* step 2 data: shows -> dates -> time blocks, all derived from the GetShowSchedule response in redux */
  const selectedShow = appointmentInfo.find((s) => String(s.showID) === String(form.showID));
  const showDates = selectedShow?.availableDates || [];
  const selectedDate = showDates.find((d) => d.appointmentDate == form.appointmentDate.date);
  const showTimes = getBookableBlocks(selectedDate);
 
  return (
    <>
    <OverlayLoader isOpen={isLoading} />
    {/* header */}
    {/* Commented as we already have a header */}
    {/* <div className='container'>
      <div className='row'>
        <div className='col-12'>
          <div className='appointment-header'>
            <Link className='appointment-logo' href="/"><img src="/images/header/logo.svg" alt="Appointment Scheduler" className="tru-nav-logo" /></Link>
          </div>
        </div>
      </div>
    </div> */}

    <div className="container appointment-schedule-container">
    <section id="appointment-wrapper">
      {form.stepComplete < 2 && <div id="appointment-section">
        <h1 className="appointment-title" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.title }}></h1>
        <p className="appointment-subtitle" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.description }}></p>

        <div className="step-container d-flex">
          <div className={`step-name ${tab == "lead" && form.stepComplete == 0?"step-active":""} ${form.stepComplete == 1?"step-active step-completed":""}`} onClick={() => displayStep('lead')} >{appointmentScheduleJSON.contactInfoTab}
            <img src="./images/appointment_schedule/checkmark-circle.svg" width="24px" height="24px"/>
          </div>
          <div className={`step-name ${tab == "appointment"?"step-active":""}`} onClick={() => { if(form.stepComplete == 1) { displayStep('appointment'); } } } style={{cursor: `${form.stepComplete == 1?"pointer": "default"}`}} >{appointmentScheduleJSON.appointmentInfoTab}</div>
        </div>

        <div id="multi-step-form">
          <div className="step step-1 appointment-tab" style={{display:`${tab == 'lead'?"block":"none"}`}}>
            <div className="d-flex flex-column" id="appointment-form-section">
              <div className="form-headings d-flex justify-content-between">
                <p className="form-title" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.leadTitle }}></p>
                <p className="form-req-text" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.informationRequired }}></p>
              </div>
              <form ref={leadFormRef} autoComplete="off" className={`appointment-form needs-validation d-flex flex-column`} onSubmit={handleSubmit((data) => onSubmit({ ...data, connectBy: "onsite" }))} noValidate id="leadForm"
                name="leadForm">
                
                <div className="appointment-field d-flex flex-column">
                  <label className="appointment-label">First Name*</label>
                  <input className={`form-control appointment-input alphaNumInput ${errors.first_name ? "input-validated":""}`} type="text" autoComplete="off" name="first_name" {...register("first_name")} disabled={form.stepComplete > 0?true:false}
                    placeholder="Enter your first name" required onChange={(e) => handleForm(e, 'first_name', null)} />
                  
                  {errors.first_name && <p className="invalid-feedback">{errors.first_name?.message}</p>}
                </div>

                <div className="appointment-field d-flex flex-column">
                  <label className="appointment-label">Last Name*</label>
                  <input className={`form-control appointment-input alphaNumInput ${errors.last_name ? "input-validated":""}`} type="text" autoComplete="off" name="last_name" {...register("last_name")} disabled={form.stepComplete > 0?true:false}
                    placeholder="Enter your last name" required onChange={(e) => handleForm(e, 'last_name', null)} />
                  
                  {errors.last_name && <p className="invalid-feedback">{errors.last_name?.message}</p>}
                </div>
                <div className="appointment-field d-flex flex-column">
                  <label className="appointment-label">Company Name*</label>
                  <input className={`form-control appointment-input alphaNumInput ${errors.company_name ? "input-validated":""}`} type="text" autoComplete="off" name="company_name" {...register("company_name")} disabled={form.stepComplete > 0?true:false}
                    placeholder="Enter your company name" onChange={(e) => handleForm(e, 'company_name', null)} />
                  
                  {errors.company_name && <p className="invalid-feedback">{errors.company_name?.message}</p>}
                </div>

                <div className="appointment-field d-flex flex-column">
                  <div className="address-label-wrapper">
                    <label className="appointment-label">Business address*</label>
                    {errors.address1 && <p className="invalid-feedback invalid-feedback-address">{errors.address1?.message}</p>}
                  </div>
                  <AddressSuggestion form={form} setIsAddressSelected={setIsAddressSelected} errors={errors} setInvalidAddressError={setInvalidAddressError} validate={register} handleInput={(val, isAddrSel) => onAddressSuggestionSelected(val, isAddrSel)} disabled={form.stepComplete > 0?true:false} />
                  
                  
                </div>

                <div className="appointment-field d-flex flex-column">
                  <label className="appointment-label">Email*</label>
                  <input className={`form-control appointment-input ${errors.email ? "input-validated":""}`} type="text" autoComplete="off" name="email" {...register("email")} placeholder="Enter your email" disabled={form.stepComplete > 0?true:false}
                    required onChange={(e) => handleForm(e, 'email', null)} />
                  
                  {errors.email && <p className="invalid-feedback">{errors.email?.message}</p>}
                </div>

                <div className="appointment-field d-flex flex-column">
                  <label className="appointment-label">Phone number*</label>
                  <input className={`form-control appointment-input ${errors.phone ? "input-validated":""}`} id="phone-number" {...register("phone")} type="text" autoComplete="off" name="phone" disabled={form.stepComplete > 0?true:false}
                    placeholder="(___)-___-____" required onChange={(e) => handleForm(e, 'phone', null)} />
                  
                  {errors.phone && <p className="invalid-feedback">{errors.phone?.message}</p>}
                </div>

                {/* -- As per requirement it is not required --
                <div className="row select-medium">
                  <div className="col-md-12">
                    <label className="on-site" tabIndex="0" >
                      <input type="radio" name="connectby" {...register("connectBy")} className="radio-input-element" value="onsite" disabled={form.stepComplete > 0?true:false} />
                      <div className="radio-input">
                        <h6 dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.connectByOnSite }}></h6>
                        <p dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.connectByOnSiteDescription }}></p>
                      </div>
                    </label>
                  </div>
                  <div className="col-md-12 mt-2">
                    <label className="phone-call" tabIndex="0" >
                      <input type="radio" name="connectby" {...register("connectBy")} className="radio-input-element" value="phonecall" disabled={form.stepComplete > 0?true:false} />
                      <div className="radio-input">
                        <h6 dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.connectByPhoneCall }}></h6>
                        <p dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.connectByPhoneCallDescription }}></p>
                      </div>
                    </label>
                  </div>
                  <div className="col-12 mt-2">
                      {errors.connectBy && <p className="invalid-callby">{errors.connectBy?.message}</p>}
                  </div>
                </div> */}
                <div className="row">
                  <div className="col-12">
                    <p className="consent-msg d-none d-md-block m-0" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.privacyConsent }}></p>
                    <p className="consent-msg d-block d-md-none m-0" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.privacyConsent }}></p>
                  </div>
                </div>  
                    {/* {outsideParams.address && (
                      <Suspense fallback={null}>
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          size="invisible"
                          sitekey={captchaSiteKey}
                        />
                      </ Suspense>
                    )} */}
                    <input type="submit" style={{display: "none"}} />
                <button type="submit" id="submitForm" ref={leadFormBtnRef} className="btn continue-btn" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.submitBtn }} tabIndex="0"  disabled={form.stepComplete > 0?true:false}></button>
              </form>
                
            </div>
          </div>

          
          <div className="step step-2 appointment-tab" style={{display:`${tab == 'appointment'?"block":"none"}`}}>
            <div className="row">
              <div className="col-6">
                <div className="top-head" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.appointmentTitle }}></div>
              </div>
              <div className="col-6 text-end">
                <div className="info-text" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.informationRequired }}></div>
              </div>
            </div>
            
            <form ref={appointmentFormRef} className="appointment-form needs-validation d-flex flex-column" onSubmit={handleSubmitAppointment(onSubmitAppointment)} noValidate id="appointmentForm"
                name="appointmentForm">
              <div className="row select-event">
                <div className="col-12">
                  <div className="sec-heading" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.preferredShowTitle }}></div>
                </div>
                {appointmentInfo.map((show) => <div className="col-6 mt-2" key={`showOption_${show.showID}`} >
                  <label className="date" tabIndex="0">
                    <input type="radio" name="showID" className="radio-input-element" value={show.showID} checked={String(form.showID) === String(show.showID)} onChange={() => handleShowSelection(show)} />
                    <div className="radio-input date-time-input">
                      <p className="date-time">{show.name}</p>
                    </div>
                  </label>
                </div>)}
              </div>
              {selectedShow && <div className="row select-date">
                <div className="col-12">
                  <div className="sec-heading" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.preferredDateTitle }}></div>
                </div>
                {showDates.map((dateInfo, i) => <div className="col-6 mt-2" key={`dateOption_${i}`} >
                  <label className="date" tabIndex="0">
                    <input type="radio" name="appointmentDate" {...registerAppointment("appointmentDate")} className="radio-input-element" value={dateInfo.appointmentDate} checked={form.appointmentDate.date == dateInfo.appointmentDate?true:false} onChange={(e) => handleDateSelection(dateInfo)} />
                    <div className="radio-input date-time-input">
                      <p className="date-time">{`${weekDayOf(dateInfo.appointmentDate)}, ${scheduleDateFormat(dateInfo.appointmentDate.slice(0, 10))}`}</p>
                    </div>
                  </label>
                </div>)}
                <div className="col-12 mt-2">
                  
                  {errorsAppointment.appointmentDate && <p className="invalid-feedback d-block">{errorsAppointment.appointmentDate?.message}</p>}
                </div>
               
              </div>}
              {showTimes.length > 0 && <div className="row select-time">
                <div className="col-12">
                  <div className="sec-heading" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.preferredTimeTitle }}></div>
                </div>
                {showTimes.map((timeInfo, j) => {
                  
                return <div className="col-12 mt-2" key={`timeOption_${j}`}>
                  <label className="time" tabIndex="0">
                    <input type="checkbox" name="appointmentTime" {...registerAppointment("appointmentTime")} id={`time_${j}`} className="radio-input-element" value={`${scheduleTime(timeInfo.startTime)} – ${scheduleTime(timeInfo.endTime)}`} checked={Object.keys(form.appointmentTime).length > 0 && form.appointmentTime.startTime == timeInfo.startTime && form.appointmentTime.endTime == timeInfo.endTime?true:false} onChange={(e) => handleTimeSelection(timeInfo)} />
                    <div className="radio-input date-time-input">
                      <p className="date-time">{scheduleTime(timeInfo.startTime).toLowerCase()} – {scheduleTime(timeInfo.endTime).toLowerCase()}</p>
                    </div>
                  </label>
                  
                </div>;
                })}
                
                
              </div>}
                  <div className="col-12">

                    {errorsAppointment.appointmentTime && <p className="invalid-feedback d-block text-center" style={{ fontSize: '18px' }}>{errorsAppointment.appointmentTime?.message}</p>}
                  </div>
              <button id="next-btn" type="submit" ref={appointmentFormBtnRef} disabled={!!errorsAppointment.appointmentTime || Object.keys(form.appointmentTime).length === 0} className="btn continue-btn" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.submitBtn }}></button>
              <div className="row">
                <div className="col-12">
                  
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>}
      <input id="visitID" type="hidden" />
      
      {form.stepComplete == 2 && <div id="thank-you-section" className="">
              <div className="mb-3 d-block d-md-none">{<img src="/images/appointment_schedule/ty-check.svg" width="65px" height="73px"/>}</div>
          <h2 className="appointment-title">Thank you, {leadForm.first_name}!</h2>
          
          {leadForm.connectBy == "success" && <><p className="appointment-subtitle">You have requested an appointment at <span>{`${leadForm.street}, ${leadForm.city}, ${leadForm.state}, ${leadForm.zipcode}`}</span> on <span>{storeAppointmentDate.weekDay}, {scheduleDateFormat(storeAppointmentDate.date)}</span> between the hours 
            of <span>{scheduleTime(storeAppointmentTime.startTime).toLowerCase()}</span> - <span>{scheduleTime(storeAppointmentTime.endTime).toLowerCase()}</span> for the event <span>{selectedShow?.name}</span>.</p>
            <p className="appointment-subtitle" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.followUp }}></p></>}

          {leadForm.connectBy == "error" && <p className="appointment-subtitle" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.schedulingNotAvailable }}></p>}

          {leadForm.connectBy == "no_schedule" && <p className="appointment-subtitle" dangerouslySetInnerHTML={{ __html: appointmentScheduleJSON.appointmentNotAvailable }}></p>}
          
      </div>}
    </section>

  </div>
    </>

    )
}

export default AppointmentSchedule;

